'use client';

import React, { useState, useEffect } from 'react';
import { useRole } from '@/contexts/RoleContext';
import { FileDown, FileText } from 'lucide-react';
import { exportCSV, exportPDF } from '@/lib/exportUtils';

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Snapshot data for dashboard export
const DASHBOARD_EXPORT_ROWS = [
  { metric: 'Total Vendors', value: '247', status: 'Active', notes: 'City of Pleasanton TPRM Registry' },
  { metric: 'Critical Risk Vendors', value: '12', status: 'Action Required', notes: 'Requires immediate review' },
  { metric: 'High Risk Vendors', value: '34', status: 'Monitor', notes: 'Elevated monitoring in place' },
  { metric: 'Contracts Expiring (30d)', value: '8', status: 'Attention', notes: 'Renewal or termination required' },
  { metric: 'Open Critical Issues', value: '5', status: 'Open', notes: 'Escalated to CISO' },
  { metric: 'DD Reviews Overdue', value: '3', status: 'Overdue', notes: 'Escalation triggered' },
  { metric: 'Insurance Certificates Expired', value: '2', status: 'Non-Compliant', notes: 'Vendors suspended pending renewal' },
  { metric: 'Avg Vendor Risk Score', value: '42/100', status: 'Moderate', notes: 'Portfolio average' },
];

const DASHBOARD_COLUMNS = [
  { header: 'Metric', key: 'metric' },
  { header: 'Value', key: 'value' },
  { header: 'Status', key: 'status' },
  { header: 'Notes', key: 'notes' },
];

export default function DashboardHeader() {
  const { can } = useRole();
  const [currentDate, setCurrentDate] = useState<string>('');

  useEffect(() => {
    setCurrentDate(formatDate(new Date()));
  }, []);

  const handleExportCSV = () => {
    exportCSV(DASHBOARD_COLUMNS, DASHBOARD_EXPORT_ROWS, `TPRM-Dashboard-${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportPDF = () => {
    exportPDF(
      'TPRM Executive Dashboard Report',
      `City of Pleasanton · As of ${currentDate}`,
      DASHBOARD_COLUMNS,
      DASHBOARD_EXPORT_ROWS
    );
  };

  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">TPRM Dashboard</h1>
        <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
          City of Pleasanton — Third-Party Risk Management{currentDate ? ` · As of ${currentDate}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5 text-2xs text-[hsl(var(--text-muted))] px-2 py-1 rounded border border-[hsl(var(--border))]">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Live · Updated 2 min ago
        </span>
        {can('dashboard:export') && (
          <>
            <button
              onClick={handleExportCSV}
              className="btn-secondary flex items-center gap-1.5"
              title="Export dashboard summary as CSV"
            >
              <FileDown size={13} />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="btn-secondary flex items-center gap-1.5"
              title="Export dashboard summary as PDF"
            >
              <FileText size={13} />
              PDF
            </button>
          </>
        )}
        {can('dashboard:newVendor') && (
          <button className="btn-primary">+ New Vendor</button>
        )}
      </div>
    </div>
  );
}
